import { FingerprintIcon, MailIcon, SaveIcon, UserIcon } from 'lucide-react';

import { useEffect, useRef, useState } from 'react';

import { userPro } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import {
  isPasskeySupported,
  isSocialOnlyUserFromToken,
} from '@splitfiesta/platform-web/auth/cognito';
import type { UpdateProfileDto } from '@splitfiesta/shared';
import { Alert, Card, FormButton, FormField } from '@splitfiesta/ui';

import { withAuth } from '@/components/auth';
import { ErrorDisplay } from '@/components/common/ErrorDisplay';
import { ImageUpload } from '@/components/common/ImageUpload';
import {
  PasswordStrengthIndicator,
  validatePassword,
} from '@/components/common/PasswordStrengthIndicator';
import { AppLayout } from '@/components/layout/AppLayout';
import { SupporterBadge } from '@/components/pro/SupporterBadge';
import { AccountDeletionSection } from '@/components/settings/AccountDeletionSection';
import { PasskeySettings } from '@/components/settings/PasskeySettings';
import { ProfilePageSkeleton } from '@/components/skeleton';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';
import {
  useChangePasswordMutation,
  useUpdateProfileMutation,
  useUserProfileQuery,
} from '@/hooks/queries/useUser';
import { useErrorHandler } from '@/hooks/useErrorHandler';

import { useAuthStore } from '@/stores/authStore';

import { toast } from '@/utils/toast';

function ProfilePage() {
  const user = useAuthStore(state => state.user);
  const idToken = useAuthStore(state => state.idToken);
  // const updateUser = useAuthStore(state => state.updateUser); // Not needed with React Query
  const { t, ready } = useTranslation();
  const { handleError } = useErrorHandler('profile');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Check if user is social-only (no password) from the JWT token
  const isSocialUser = idToken ? isSocialOnlyUserFromToken(idToken) : null;

  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  // Timeout refs for cleanup
  const profileSuccessTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const passwordSuccessTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  // Load required namespaces
  const profileReady = useNamespace('profile');
  const commonReady = useNamespace('common');
  const membersReady = useNamespace('members');
  const namespacesLoaded = profileReady && commonReady;
  const proEnabled = useFeatureFlag('pro');
  const showSupporterNote = membersReady && proEnabled && userPro(user).active;

  // Use React Query for profile data
  const {
    data: profile,
    isLoading: loading,
    error,
  } = useUserProfileQuery(ready && namespacesLoaded && !!user);

  // Use mutations for updates
  const updateProfileMutation = useUpdateProfileMutation();
  const changePasswordMutation = useChangePasswordMutation();

  // Initialize form state when profile loads
  useEffect(() => {
    if (profile) {
      // Wrap setState in setTimeout to avoid direct state setting in effect
      const timer = setTimeout(() => {
        setName(prev => {
          const newName = profile.name || '';
          if (prev !== newName) return newName;
          return prev;
        });
        setEmail(prev => {
          const newEmail = profile.email || '';
          if (prev !== newEmail) return newEmail;
          return prev;
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [profile]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (profileSuccessTimeout.current) {
        clearTimeout(profileSuccessTimeout.current);
      }
      if (passwordSuccessTimeout.current) {
        clearTimeout(passwordSuccessTimeout.current);
      }
    };
  }, []);

  // Handle profile update
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error(t('profile:errors.nameRequired'));
      return;
    }

    const updateData: UpdateProfileDto = {
      name: name.trim(),
    };

    try {
      // Update profile
      await updateProfileMutation.mutateAsync(updateData);

      setProfileSuccess(t('profile:messages.profileUpdated'));
      // Clear any existing timeout
      if (profileSuccessTimeout.current) {
        clearTimeout(profileSuccessTimeout.current);
      }
      profileSuccessTimeout.current = setTimeout(
        () => setProfileSuccess(''),
        3000
      );
    } catch (err) {
      const errorMessage = handleError(err);
      toast.error(errorMessage);
    }
  };

  const saving = updateProfileMutation.isPending;
  const profileError = updateProfileMutation.error
    ? handleError(updateProfileMutation.error)
    : null;

  // Handle password change
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('profile:errors.allFieldsRequired'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('profile:errors.passwordMismatch'));
      return;
    }
    // Use the comprehensive password validation
    const { isValid, errors } = validatePassword(newPassword);
    if (!isValid) {
      setPasswordError(t(errors[0]));
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword,
        newPassword,
      });

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
      setPasswordSuccess(t('profile:messages.passwordChanged'));
      // Clear any existing timeout
      if (passwordSuccessTimeout.current) {
        clearTimeout(passwordSuccessTimeout.current);
      }
      passwordSuccessTimeout.current = setTimeout(
        () => setPasswordSuccess(''),
        3000
      );
    } catch (err) {
      // Handle specific Cognito errors for social-only users
      const errorMessage =
        err instanceof Error ? err.message.toLowerCase() : '';
      if (
        errorMessage.includes('password') &&
        (errorMessage.includes('not set') ||
          errorMessage.includes('no password') ||
          errorMessage.includes('invalid'))
      ) {
        setPasswordError(
          t('profile:errors.socialLoginNoPassword', {
            defaultValue:
              'Password change is not available for accounts created with social login (Google, Apple). Please use your social provider to manage authentication.',
          })
        );
      } else {
        setPasswordError(handleError(err));
      }
    }
  };

  const [changingPassword, setChangingPassword] = useState(false);

  if (!user) {
    return null;
  }

  // Show skeleton immediately while translations or data are loading
  if (!ready || !namespacesLoaded || loading) {
    return (
      <AppLayout>
        <ProfilePageSkeleton />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('profile:title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('profile:subtitle')}
          </p>
        </div>

        {/* Error State */}
        {(error || profileError) && (
          <ErrorDisplay
            data-testid="profile-error"
            error={(() => {
              const err = error || profileError;
              if (typeof err === 'string') {
                return err;
              }
              if (err && typeof err === 'object' && 'message' in err) {
                return (err as Error).message;
              }
              return 'An error occurred';
            })()}
            type="inline"
          />
        )}

        <div
          className={
            isSocialUser === false
              ? 'grid grid-cols-1 lg:grid-cols-2 gap-8'
              : 'max-w-xl mx-auto'
          }
        >
          {/* Profile Information */}
          <Card data-testid="profile-basic-info-card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('profile:sections.basicInfo')}
            </h3>

            {profileSuccess && (
              <Alert type="success" message={profileSuccess} className="mb-4" />
            )}

            <form
              onSubmit={handleUpdateProfile}
              className="space-y-4"
              data-testid="profile-form"
            >
              <FormField
                id="name"
                type="text"
                label={t('profile:fields.name')}
                value={name}
                onChange={setName}
                placeholder={t('profile:placeholders.name')}
                required
                disabled={saving}
                maxLength={100}
                icon={UserIcon}
                autoComplete="off"
                data-testid="profile-name-input"
              />
              {showSupporterNote && (
                <p
                  className="-mt-2 flex items-start gap-1.5 text-sm text-gray-500 dark:text-gray-400"
                  data-testid="profile-supporter-note"
                >
                  <SupporterBadge className="mt-0.5" />
                  <span>{t('members:supporter.selfNote')}</span>
                </p>
              )}

              <FormField
                id="email"
                type="email"
                label={t('profile:fields.email')}
                value={email}
                onChange={() => {
                  // Read-only field - email cannot be changed
                }}
                placeholder={t('profile:placeholders.email')}
                required
                disabled={true} // Always disabled for security
                icon={MailIcon}
                helpText={t('profile:help.emailChangeRequiresVerification')}
                data-testid="profile-email-input"
              />

              {/* Profile Picture Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {t('profile:fields.avatar')}
                </label>
                <ImageUpload
                  type="profile"
                  entityId={user.id}
                  currentImageUrl={profile?.profileImage}
                  data-testid="profile-avatar-upload"
                  onImageUploaded={async imageUrl => {
                    // Update profile with new avatar
                    try {
                      await updateProfileMutation.mutateAsync({
                        name: name.trim(),
                        profileImage: imageUrl,
                      });
                      toast.success(t('profile:messages.profileImageUpdated'));
                    } catch (err) {
                      toast.error(handleError(err));
                    }
                  }}
                  onImageDeleted={async () => {
                    // Delete profile image
                    try {
                      await updateProfileMutation.mutateAsync({
                        name: name.trim(),
                        profileImage: null,
                      });
                      toast.success(t('profile:messages.profileImageDeleted'));
                    } catch (err) {
                      toast.error(handleError(err));
                      throw err; // Re-throw to let ImageUpload handle the error state
                    }
                  }}
                />
              </div>

              <FormButton
                type="submit"
                disabled={!name.trim()}
                loading={saving}
                loadingText={t('common:actions.saving')}
                icon={SaveIcon}
                fullWidth
                data-testid="profile-save-button"
              >
                {t('profile:actions.updateProfile')}
              </FormButton>
            </form>
          </Card>

          {/* Security Section - Passkeys and Password (only for non-social users) */}
          {isSocialUser === false && (
            <div className="space-y-6">
              {/* Passkeys - Show for non-social users if browser supports it */}
              {isPasskeySupported() && (
                <Card data-testid="passkeys-card">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <FingerprintIcon className="w-5 h-5 mr-2" />
                    {t('profile:sections.passkeys')}
                  </h3>
                  <PasskeySettings data-testid="passkey-settings" />
                </Card>
              )}

              {/* Change Password - Only show for email/password users */}
              <Card data-testid="change-password-card">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {t('profile:sections.changePassword')}
                </h3>

                {passwordError && (
                  <Alert
                    type="error"
                    message={passwordError}
                    className="mb-4"
                  />
                )}

                {passwordSuccess && (
                  <Alert
                    type="success"
                    message={passwordSuccess}
                    className="mb-4"
                  />
                )}

                <form
                  onSubmit={async e => {
                    setChangingPassword(true);
                    await handleChangePassword(e);
                    setChangingPassword(false);
                  }}
                  className="space-y-4"
                  data-testid="password-form"
                >
                  <FormField
                    id="currentPassword"
                    type="password"
                    label={t('profile:fields.currentPassword')}
                    value={currentPassword}
                    onChange={setCurrentPassword}
                    placeholder={t('profile:placeholders.currentPassword')}
                    required
                    disabled={changingPassword}
                    autoComplete="off"
                    data-testid="current-password-input"
                  />

                  <div className="space-y-2">
                    <FormField
                      id="newPassword"
                      type="password"
                      label={t('profile:fields.newPassword')}
                      value={newPassword}
                      onChange={setNewPassword}
                      placeholder={t('profile:placeholders.newPassword')}
                      required
                      disabled={changingPassword}
                      autoComplete="off"
                      data-testid="new-password-input"
                    />
                    <PasswordStrengthIndicator
                      password={newPassword}
                      className="mt-2"
                    />
                  </div>

                  <div className="space-y-2">
                    <FormField
                      id="confirmPassword"
                      type="password"
                      label={t('profile:fields.confirmPassword')}
                      value={confirmPassword}
                      onChange={setConfirmPassword}
                      placeholder={t('profile:placeholders.confirmPassword')}
                      required
                      disabled={changingPassword}
                      autoComplete="off"
                      data-testid="confirm-password-input"
                    />
                    {confirmPassword && (
                      <PasswordStrengthIndicator
                        password={newPassword}
                        confirmPassword={confirmPassword}
                        showConfirmMatch={true}
                        compact={true}
                        className="mt-2"
                      />
                    )}
                  </div>

                  <FormButton
                    type="submit"
                    disabled={
                      !currentPassword || !newPassword || !confirmPassword
                    }
                    loading={changingPassword}
                    loadingText={t('common:actions.saving')}
                    icon={SaveIcon}
                    fullWidth
                    data-testid="change-password-button"
                  >
                    {t('profile:actions.changePassword')}
                  </FormButton>
                </form>
              </Card>
            </div>
          )}
        </div>

        {/* Danger zone — mirrors mobile: the deletion entry lives on the
            profile as well as Settings, for EVERY user (social included).
            Width matches the layout above: full grid width for
            email/password users, the narrow centered column for social. */}
        <div
          className={isSocialUser === false ? 'mt-8' : 'mt-8 max-w-xl mx-auto'}
        >
          <AccountDeletionSection />
        </div>
      </div>
    </AppLayout>
  );
}

export default withAuth(ProfilePage);
